/**
 * What a shoot costs — the owner's 2026 model, as one function.
 *
 * ── THE MODEL IN FOUR LINES ──────────────────────────────────────────────
 *
 *     dopłata   = podana wprost  albo  max(0, jednostki − w cenie) × za kolejną
 *     wykonawca = ⌈(baza + zdjęcia × za zdjęcie + dopłata) / 10 zł⌉ × 10 zł
 *     agencja   = ⌈wykonawca / (1 − marża) / 10 zł⌉ × 10 zł,  dla obu końców
 *     klient    = okrągłe 50 zł od średniej z tych dwóch końców
 *
 * The last line is the one that surprises people, so it is worth stating: the
 * client's price is the MIDDLE of the 20–25% band, not either end, and each end
 * is rounded UP to 10 zł before they are averaged. That is why a realised margin
 * comes out at 22.2% or 23.8% rather than at 20 or 25 — and why recomputing it
 * any other way disagrees with the owner's spreadsheet by a few złoty on about
 * a third of the rows.
 *
 * ── WHY IT LIVES IN `@repo/db` ───────────────────────────────────────────
 * The same reason `shoot-plans.ts` does, plus one more. The checkout
 * transaction has to price a line INSIDE the transaction, so it cannot reach
 * for `@repo/core`; and `/pl/fotograf-operator` is a client component, which
 * cannot import `@repo/core` at all — it would pull the database into the
 * browser bundle. Both of them can import this, because it is arithmetic.
 *
 * Pure and dependency-free. Every number in and out is NET grosze.
 */

/** One row of `shoot_rate`, reduced to what the arithmetic reads. */
export type ShootRate = {
  baseNetMinor: number
  perPhotoNetMinor: number
  includedUnits: number
  extraUnitNetMinor: number
}

/** One row of `shoot_scope`, same. */
export type ShootScope = {
  /** A head or product count, or `null` for a band that is not a count. */
  units: number | null
  /** Stated instead of derived — the property session's floor-area bands. */
  surchargeNetMinor: number | null
}

/**
 * The margin the agency takes, and how the two figures are rounded.
 *
 * Basis points rather than a fraction, because a percentage stored as `0.225`
 * is a rounding error waiting for somebody to type it, and every other rate in
 * this schema (`tax_rate_bps`) is already bps.
 */
export type ShootMargin = {
  lowBps: number
  highBps: number
  performerStepMinor: number
  clientStepMinor: number
}

export type ShootPriceBreakdown = {
  /** The day itself, before photos and before anybody is counted. */
  baseNetMinor: number
  /** `photos × rate`, the part the visitor moves by choosing a bigger package. */
  photosNetMinor: number
  /** What the extra people, products or square metres add. */
  surchargeNetMinor: number
  /** What the photographer is paid — the sum above, rounded up to 10 zł. */
  performerNetMinor: number
  /** What the client pays. */
  clientNetMinor: number
  /** `(client − performer) / client`, in basis points. FLOU's actual cut. */
  marginBps: number
}

/** `⌈value / step⌉ × step`. A price that lands on 1 401 zł is sold at 1 410. */
function roundUpTo(value: number, step: number) {
  if (step <= 0) return Math.round(value)
  return Math.ceil(value / step - 1e-9) * step
}

/**
 * `round(value / step) × step`, half away from zero.
 *
 * Excel's ROUND, deliberately — this reproduces a spreadsheet, and JavaScript's
 * `Math.round` breaks ties upward instead, which disagrees on every negative
 * number and on exact halves. There are no negative prices, but a function that
 * matches its source only in the cases that happen to occur is a trap for
 * whoever edits it next.
 */
function roundHalfUpTo(value: number, step: number) {
  if (step <= 0) return Math.round(value)
  const units = value / step
  return (units >= 0 ? Math.floor(units + 0.5) : Math.ceil(units - 0.5)) * step
}

/**
 * What this scope adds to the base rate.
 *
 * Two ways of saying it, and the scope picks which: a count charges per unit
 * past the ones the base covers, a band states its figure. Neither is a special
 * case of the other — 150 m² is not 150 units of anything.
 */
export function shootSurchargeNetMinor(rate: ShootRate, scope: ShootScope) {
  if (scope.surchargeNetMinor !== null) return scope.surchargeNetMinor
  if (scope.units === null) return 0
  return Math.max(0, scope.units - rate.includedUnits) * rate.extraUnitNetMinor
}

/**
 * Price one shoot: a session's rate, the scope chosen, and how many photos.
 *
 * `photos` is one of the three the scope offers; nothing here checks that,
 * because the caller is the one holding the list and a price for a number
 * nobody offered is still the right price for that number.
 */
export function priceShoot(
  rate: ShootRate,
  scope: ShootScope,
  photos: number,
  margin: ShootMargin,
): ShootPriceBreakdown {
  const photosNetMinor = photos * rate.perPhotoNetMinor
  const surchargeNetMinor = shootSurchargeNetMinor(rate, scope)
  const performerNetMinor = roundUpTo(
    rate.baseNetMinor + photosNetMinor + surchargeNetMinor,
    margin.performerStepMinor,
  )

  // Each end of the band separately, each rounded UP, and only then averaged.
  // Averaging first and dividing once gives a different number on about a third
  // of the rows — the two roundings do not commute with the mean.
  const low = roundUpTo(performerNetMinor / (1 - margin.lowBps / 10000), margin.performerStepMinor)
  const high = roundUpTo(
    performerNetMinor / (1 - margin.highBps / 10000),
    margin.performerStepMinor,
  )
  const clientNetMinor = roundHalfUpTo((low + high) / 2, margin.clientStepMinor)

  return {
    baseNetMinor: rate.baseNetMinor,
    photosNetMinor,
    surchargeNetMinor,
    performerNetMinor,
    clientNetMinor,
    marginBps:
      clientNetMinor > 0
        ? Math.round(((clientNetMinor - performerNetMinor) / clientNetMinor) * 10000)
        : 0,
  }
}

/** The key both tables are read by: the session, and nothing else. */
export type ShootRateKey = { session: string }

/**
 * Where the margin lives: one `site_setting` row, not a table of one.
 *
 * Two numbers that apply to everything have no natural table — and `site_setting`
 * is already where the travel rate sits, for the same reason and by the same
 * decision (spec §12: „stawka edytowalna przez Admina").
 */
export const SHOOT_MARGIN_KEY = 'shoot-pricing.margin'

/** Read a stored margin back, falling back to the model's own defaults. */
export function parseShootMargin(value: unknown, fallback: ShootMargin): ShootMargin {
  if (!value || typeof value !== 'object') return fallback
  const raw = value as Record<string, unknown>
  const num = (key: keyof ShootMargin) =>
    typeof raw[key] === 'number' && Number.isFinite(raw[key]) ? (raw[key] as number) : fallback[key]
  return {
    lowBps: num('lowBps'),
    highBps: num('highBps'),
    performerStepMinor: num('performerStepMinor'),
    clientStepMinor: num('clientStepMinor'),
  }
}
